import { Clock, MapPin, MessageCircle, Phone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { IMAGES, SITE, WA_DEFAULT } from "../../lib/site";
import { Eyebrow, Reveal } from "./Reveal";

const HOURS = [
    { day: "Lunedì — Domenica", time: "6:15 — 20:00" },
];

export default function Contact() {
    const downloadQR = () => {
        const svg = document.querySelector("[data-testid='contact-qr-code'] svg");
        if (!svg) return;
        const data = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = canvas.height = 1024;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, 1024, 1024);
            ctx.drawImage(img, 0, 0, 1024, 1024);
            const a = document.createElement("a");
            a.download = "kore-qrcode.png";
            a.href = canvas.toDataURL("image/png");
            a.click();
        };
        img.src =
            "data:image/svg+xml;base64," +
            btoa(unescape(encodeURIComponent(data)));
    };

    return (
        <section id="contatti" data-testid="contact-location-section" className="border-b border-hairline bg-linen px-5 py-24 sm:px-8 md:py-32">
            <div className="mx-auto max-w-6xl">
                <Reveal>
                    <Eyebrow>Dove trovarci</Eyebrow>
                    <h2 className="mt-6 max-w-lg text-3xl font-light leading-tight tracking-[-0.02em] text-ink sm:text-5xl">
                        A due passi dal lungolago
                    </h2>
                </Reveal>

                <div className="mt-12 grid gap-3 md:grid-cols-2">
                    <Reveal>
                        <div className="flex h-full flex-col border border-hairline bg-white p-8">
                            <div className="flex items-start gap-5">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-hairline text-ink">
                                    <MapPin className="h-5 w-5" strokeWidth={1.5} />
                                </span>
                                <div>
                                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-ink">
                                        Kore Studio Pilates
                                    </h3>
                                    <p data-testid="contact-map-address" className="mt-1.5 text-sm font-light text-body">
                                        {SITE.address}
                                    </p>
                                    <a
                                        href={SITE.mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-maps-link"
                                        className="mt-2 inline-block text-xs font-semibold uppercase tracking-[0.16em] text-ochre underline-offset-4 hover:underline"
                                    >
                                        Apri in Google Maps
                                    </a>
                                </div>
                            </div>

                            <div className="mt-10 flex items-start gap-5 border-t border-hairline pt-10">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-hairline text-ink">
                                    <Clock className="h-5 w-5" strokeWidth={1.5} />
                                </span>
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-ink">
                                        Orari di apertura
                                    </h4>
                                    <ul className="mt-3 space-y-2">
                                        {HOURS.map((h) => (
                                            <li key={h.day} className="flex items-center justify-between text-sm font-light">
                                                <span className="text-body">{h.day}</span>
                                                <span className="font-normal text-ink">{h.time}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <p className="mt-10 border border-hairline bg-linen p-4 text-xs font-light leading-relaxed text-body">
                                Parcheggio consigliato in Piazza Risorgimento, a
                                due minuti a piedi. Lo studio è nel centro di
                                Cernobbio, su Via Matteotti.
                            </p>

                            <div className="mt-10 flex items-center gap-5 border-t border-hairline pt-10">
                                <div
                                    data-testid="contact-qr-code"
                                    className="shrink-0 border border-hairline bg-white p-2"
                                >
                                    <QRCodeSVG
                                        value={window.location.origin}
                                        size={96}
                                        bgColor="#FFFFFF"
                                        fgColor="#121212"
                                        level="M"
                                    />
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-ink">
                                        QR per lo studio
                                    </h4>
                                    <p className="mt-1.5 text-xs font-light leading-relaxed text-body">
                                        Stampalo e tienilo al banco o in vetrina: i
                                        clienti lo inquadrano e prenotano in un secondo.
                                    </p>
                                    <button
                                        data-testid="contact-qr-download"
                                        onClick={downloadQR}
                                        className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-ochre underline-offset-4 hover:underline"
                                    >
                                        Scarica per la stampa
                                    </button>
                                </div>
                            </div>

                            <div className="mt-auto flex flex-col gap-3 pt-10 sm:flex-row">
                                <a
                                    href={WA_DEFAULT}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-link"
                                    className="flex flex-1 items-center justify-center gap-2 bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98]"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    WhatsApp {SITE.whatsappDisplay}
                                </a>
                                <a
                                    href={SITE.phoneHref}
                                    data-testid="contact-phone-link"
                                    className="flex flex-1 items-center justify-center gap-2 border border-ink py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-200 hover:bg-ink hover:text-white active:scale-[0.98]"
                                >
                                    <Phone className="h-4 w-4" />
                                    {SITE.phone}
                                </a>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <div className="relative h-full min-h-[22rem] overflow-hidden border border-hairline">
                            <img
                                src={IMAGES.panorama}
                                alt="Panorama del Lago di Como presso Cernobbio"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 border-t border-hairline bg-white/90 p-6 backdrop-blur-md">
                                <p className="font-accent text-xl font-medium italic text-ink">
                                    «Il lago fuori, la calma dentro.»
                                </p>
                                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-mute">
                                    Cernobbio · Lago di Como
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
